/**
 * The brand asset library used by the Brand Assets page (manage mode) and the AssetPicker modal
 * (pick mode). PlayAd controls: "Content ▾" kind filter · Search · ♡ favourites · purple "Import" ·
 * "↕ Newest First ▾"; a "Logos" strip; then the asset grid.
 *
 * Tools:
 *  - assets.list {brandId, kind?, search?, favorite?, limit: 200} → {total, items: BrandAsset[]}
 *      (server orders favourites first, then newest; "Newest/Oldest/Name" sorting is applied client-side)
 *  - assets.update {brandId, assetId, favorite?|archived?}   (heart / archive from the tile menu)
 *  - assets.delete {brandId, assetId}                         (gated — confirmation modal)
 */
import { Archive, ArrowDownUp, ExternalLink, Heart, ImagePlus, LayoutGrid, Pencil, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTool, useToolMutation } from "@/api/hooks";
import { cn } from "@/lib/cn";
import { Button } from "../ui/Button";
import { EmptyState, ErrorState, Skeleton } from "../ui/Feedback";
import { Chip, SearchInput, SelectPill, type SelectOption } from "../ui/Form";
import { MenuItem, MenuSeparator } from "../ui/Overlay";
import { useDebounced } from "../creative/hooks";
import { AssetTile } from "./AssetTile";
import { ASSET_KIND_LABEL, ASSET_KINDS, normalizeAssets, type AssetKind, type BrandAsset } from "./normalize";
import { safeMediaHref } from "@/lib/safe-url";

export type AssetSort = "newest" | "oldest" | "name";

const SORT_OPTIONS: SelectOption<AssetSort>[] = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "name", label: "Name A–Z" },
];

export function useAssetMutations(brandId: string) {
  const [optimistic, setOptimistic] = useState<Record<string, Partial<BrandAsset>>>({});
  const update = useToolMutation("assets.update", {
    silentError: false,
    onError: () => setOptimistic({}),
  });
  const del = useToolMutation("assets.delete", { success: "Asset deleted" });
  return {
    optimistic,
    toggleFavorite: (a: BrandAsset) => {
      const fav = !(optimistic[a.id]?.favorite ?? a.favorite);
      setOptimistic((o) => ({ ...o, [a.id]: { ...o[a.id], favorite: fav } }));
      update.mutate({ brandId, assetId: a.id, favorite: fav });
    },
    archive: (a: BrandAsset) => {
      setOptimistic((o) => ({ ...o, [a.id]: { ...o[a.id], archived: true } }));
      update.mutate({ brandId, assetId: a.id, archived: true });
    },
    remove: (a: BrandAsset) => del.mutate({ brandId, assetId: a.id }),
  };
}

export function AssetLibrary({
  brandId,
  mode,
  selected,
  onToggle,
  kinds,
  onOpen,
  onImport,
  className,
  gridClassName,
  onCount,
}: {
  brandId: string;
  mode: "manage" | "pick";
  selected: BrandAsset[];
  onToggle: (a: BrandAsset) => void;
  /** Restrict to these kinds (picker). */
  kinds?: string[];
  onOpen?: (a: BrandAsset) => void;
  onImport: () => void;
  className?: string;
  gridClassName?: string;
  /** Reports the number of assets currently listed (page header count). */
  onCount?: (n: number | null) => void;
}) {
  const allowed = useMemo(() => (kinds?.length ? ASSET_KINDS.filter((k) => kinds.includes(k)) : [...ASSET_KINDS]), [kinds]);
  const [kind, setKind] = useState<AssetKind | "all">(allowed.length === 1 ? (allowed[0] as AssetKind) : "all");
  const [search, setSearch] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [sort, setSort] = useState<AssetSort>("newest");
  const query = useDebounced(search.trim(), 300);
  const input = {
    brandId,
    ...(kind !== "all" ? { kind } : {}),
    ...(query ? { search: query } : {}),
    ...(favOnly ? { favorite: true } : {}),
    limit: 200,
  };
  const q = useTool<unknown>("assets.list", brandId ? input : null, { keepPrevious: false });
  const m = useAssetMutations(brandId);

  const assets = useMemo(() => {
    let list = normalizeAssets(q.data).map((a) => ({ ...a, ...m.optimistic[a.id] }));
    list = list.filter((a) => !a.archived && allowed.includes(a.kind));
    if (kind !== "all") list = list.filter((a) => a.kind === kind);
    if (favOnly) list = list.filter((a) => a.favorite);
    if (query) {
      const needle = query.toLowerCase();
      list = list.filter((a) => [a.name, a.description ?? "", ...a.tags, a.kind].join(" ").toLowerCase().includes(needle));
    }
    const t = (a: BrandAsset) => (a.createdAt ? Date.parse(a.createdAt) : 0);
    list.sort((a, b) => (sort === "name" ? (a.name || a.kind).localeCompare(b.name || b.kind) : sort === "oldest" ? t(a) - t(b) : t(b) - t(a)));
    return list;
  }, [q.data, m.optimistic, allowed, kind, favOnly, query, sort]);

  const listed = q.data === undefined ? null : assets.length;
  useEffect(() => onCount?.(listed), [listed, onCount]);

  const showLogoStrip = kind === "all" && allowed.includes("logo");
  const logos = showLogoStrip ? assets.filter((a) => a.kind === "logo") : [];
  const grid = showLogoStrip ? assets.filter((a) => a.kind !== "logo") : assets;
  const selectedIds = new Set(selected.map((a) => a.id));
  const kindOptions: SelectOption<AssetKind | "all">[] = [
    ...(allowed.length > 1 ? [{ value: "all" as const, label: "All content" }] : []),
    ...allowed.map((k) => ({ value: k, label: ASSET_KIND_LABEL[k] })),
  ];
  const filtered = kind !== "all" || !!query || favOnly;

  const tile = (a: BrandAsset, compact?: boolean) => (
    <AssetTile
      key={a.id}
      asset={a}
      selected={selectedIds.has(a.id)}
      selectable={mode === "pick"}
      onToggleSelect={() => onToggle(a)}
      onOpen={mode === "manage" && onOpen ? () => onOpen(a) : undefined}
      onFavorite={() => m.toggleFavorite(a)}
      showCaption={!compact}
      menu={
        mode === "manage"
          ? (close) => (
              <>
                {onOpen && (
                  <MenuItem
                    icon={<Pencil className="size-3.5" />}
                    onClick={() => {
                      close();
                      onOpen(a);
                    }}
                  >
                    Edit details
                  </MenuItem>
                )}
                <MenuItem
                  icon={<Heart className="size-3.5" />}
                  onClick={() => {
                    close();
                    m.toggleFavorite(a);
                  }}
                >
                  {a.favorite ? "Remove favourite" : "Favourite"}
                </MenuItem>
                <MenuItem
                  icon={<ExternalLink className="size-3.5" />}
                  onClick={() => {
                    close();
                    const href = safeMediaHref(a.url);
                    if (href) window.open(href, "_blank", "noopener,noreferrer");
                  }}
                >
                  Open original
                </MenuItem>
                <MenuSeparator />
                <MenuItem
                  icon={<Archive className="size-3.5" />}
                  onClick={() => {
                    close();
                    m.archive(a);
                  }}
                >
                  Archive
                </MenuItem>
                <MenuItem
                  danger
                  icon={<Trash2 className="size-3.5" />}
                  onClick={() => {
                    close();
                    m.remove(a);
                  }}
                >
                  Delete
                </MenuItem>
              </>
            )
          : undefined
      }
      className={compact ? "w-[88px] shrink-0" : undefined}
    />
  );

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex flex-wrap items-center gap-2">
        {kindOptions.length > 1 && (
          <SelectPill value={kind} options={kindOptions} onChange={setKind} label={kind === "all" ? "Content" : ASSET_KIND_LABEL[kind]} icon={<LayoutGrid className="size-3.5" strokeWidth={1.75} />} />
        )}
        <SearchInput value={search} onChange={setSearch} placeholder="Search" className="w-full min-w-[160px] flex-1 sm:max-w-[240px]" />
        <Chip selected={favOnly} onClick={() => setFavOnly((v) => !v)} icon={<Heart className="size-3.5" fill={favOnly ? "currentColor" : "none"} />}>
          Favourites
        </Chip>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="primary" size="sm" icon={<ImagePlus className="size-3.5" strokeWidth={1.75} />} onClick={onImport}>
            Import
          </Button>
          <SelectPill value={sort} options={SORT_OPTIONS} onChange={setSort} align="end" icon={<ArrowDownUp className="size-3.5" strokeWidth={1.75} />} />
        </div>
      </div>

      {q.isLoading ? (
        <LibrarySkeleton logos={showLogoStrip} className={gridClassName} />
      ) : q.error && !q.data ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          {showLogoStrip && (
            <div className="flex min-h-[72px] items-center gap-4 rounded-xl border border-line bg-surface px-4 py-3 sm:max-w-full">
              <span className="shrink-0 text-[14px] font-medium">Logos</span>
              {logos.length === 0 ? (
                <span className="ml-auto text-[12.5px] text-muted">No logos yet</span>
              ) : (
                <div className="flex min-w-0 flex-1 gap-2.5 overflow-x-auto py-0.5 scrollbar-thin">{logos.map((a) => tile(a, true))}</div>
              )}
            </div>
          )}
          {grid.length === 0 ? (
            filtered ? (
              <EmptyState compact title="No assets match" body="Try a different content type or search." />
            ) : (
              <button
                type="button"
                onClick={onImport}
                className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-line-strong px-6 py-12 text-center transition-colors hover:border-primary/60 hover:bg-primary-soft/30"
              >
                <ImagePlus className="size-6 text-muted" strokeWidth={1.5} />
                <span className="text-[14px] font-medium">Import your brand assets</span>
                <span className="max-w-sm text-[12.5px] text-muted">
                  Logos, product shots, app screenshots and lifestyle photos. Mimic uses them as reference images so every ad looks like your brand.
                </span>
              </button>
            )
          ) : (
            <div className={cn("grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3", gridClassName)}>{grid.map((a) => tile(a))}</div>
          )}
        </>
      )}
    </div>
  );
}

function LibrarySkeleton({ logos, className }: { logos: boolean; className?: string }) {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      {logos && <Skeleton className="h-[72px] rounded-xl" />}
      <div className={cn("grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3", className)}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <Skeleton className="aspect-square rounded-xl" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </div>
    </div>
  );
}
